import {
  getDefaultLanguageSettings,
  getSystemLanguageOptions,
  normalizeLanguageSettings,
} from '../../../../packages/src/localization/languageSettings.js';
import { alertMessagePopup } from '../views/modals/others.js';

const EVENT_LANGUAGE_SETTINGS_SELECTOR = '#eventLanguageSettings';
const EVENT_LANGUAGE_TAB_SELECTOR = '.nav-tabs a[href="#eventLanguages"]';
const EVENT_LANGUAGE_VALIDATION_MESSAGE = 'Please enable at least one language.';

function getLanguageFieldId(kind, code) {
  return `eventLanguage${kind}-${code}`;
}

function renderEventLanguageSettings() {
  const container = document.querySelector(EVENT_LANGUAGE_SETTINGS_SELECTOR);
  if (!container) {
    return;
  }

  container.innerHTML = getSystemLanguageOptions()
    .map(({ code, label }) => {
      const enabledId = getLanguageFieldId('Enabled', code);
      const defaultId = getLanguageFieldId('Default', code);

      return `
        <section class="event-language-card" data-language-code="${code}" data-enabled="false" data-default="false">
          <div class="event-language-card__header">
            <div class="event-language-card__title-row">
              <h6 class="event-language-card__title">${label}</h6>
              <span class="event-language-card__code">${code.toUpperCase()}</span>
            </div>
          </div>
          <div class="event-language-card__controls">
            <div class="custom-control custom-switch event-language-switch">
              <input class="custom-control-input event-language-enabled" type="checkbox"
                     id="${enabledId}" data-language-code="${code}" aria-label="Enable ${label}">
              <label class="custom-control-label event-language-switch__label" for="${enabledId}"></label>
            </div>
            <div class="event-language-default-control">
              <input class="event-language-default event-language-default__input" type="radio"
                     name="eventDefaultLanguage" id="${defaultId}" data-language-code="${code}">
              <label class="event-language-default__pill" for="${defaultId}">Use as default</label>
            </div>
          </div>
          <div class="event-language-card__hint"></div>
        </section>
      `;
    })
    .join('');
}

function getEnabledLanguageInput(code) {
  return document.querySelector(
    `${EVENT_LANGUAGE_SETTINGS_SELECTOR} .event-language-enabled[data-language-code="${code}"]`
  );
}

function getDefaultLanguageInput(code) {
  return document.querySelector(
    `${EVENT_LANGUAGE_SETTINGS_SELECTOR} .event-language-default[data-language-code="${code}"]`
  );
}

function getEnabledLanguageCodesFromForm() {
  return getSystemLanguageOptions()
    .map(({ code }) => code)
    .filter((code) => getEnabledLanguageInput(code)?.checked);
}

function syncEventLanguageEnabledSelection() {
  const enabledLanguages = getEnabledLanguageCodesFromForm();
  const protectedLanguageCode = enabledLanguages.length === 1 ? enabledLanguages[0] : null;

  getSystemLanguageOptions().forEach(({ code }) => {
    const enabledInput = getEnabledLanguageInput(code);
    if (!enabledInput) {
      return;
    }

    enabledInput.disabled = enabledInput.checked && protectedLanguageCode === code;
  });
}

function updateEventLanguageCardsState() {
  getSystemLanguageOptions().forEach(({ code }) => {
    const card = document.querySelector(`${EVENT_LANGUAGE_SETTINGS_SELECTOR} [data-language-code="${code}"]`);
    const enabledInput = getEnabledLanguageInput(code);
    const defaultInput = getDefaultLanguageInput(code);
    const hint = card?.querySelector('.event-language-card__hint');

    if (!card || !enabledInput || !defaultInput || !hint) {
      return;
    }

    card.dataset.enabled = String(Boolean(enabledInput.checked));
    card.dataset.default = String(Boolean(defaultInput.checked));
    card.dataset.locked = String(Boolean(enabledInput.disabled && enabledInput.checked));
    hint.textContent = '';
  });
}

function getCheckedDefaultLanguageFromForm({ includeDisabled = true } = {}) {
  const defaultSelector = includeDisabled
    ? `${EVENT_LANGUAGE_SETTINGS_SELECTOR} .event-language-default:checked`
    : `${EVENT_LANGUAGE_SETTINGS_SELECTOR} .event-language-default:checked:not(:disabled)`;

  return document.querySelector(defaultSelector)?.dataset.languageCode || null;
}

function syncEventLanguageDefaultSelection(preferredLanguage = null) {
  const enabledLanguages = getEnabledLanguageCodesFromForm();
  const currentDefaultLanguage = getCheckedDefaultLanguageFromForm();

  syncEventLanguageEnabledSelection();

  getSystemLanguageOptions().forEach(({ code }) => {
    const defaultInput = getDefaultLanguageInput(code);
    if (!defaultInput) {
      return;
    }

    const isEnabled = enabledLanguages.includes(code);
    defaultInput.disabled = !isEnabled;
    if (!isEnabled) {
      defaultInput.checked = false;
    }
  });

  if (enabledLanguages.length === 0) {
    return;
  }

  let nextDefaultLanguage = enabledLanguages[0];
  if (enabledLanguages.includes(preferredLanguage)) {
    nextDefaultLanguage = preferredLanguage;
  } else if (enabledLanguages.includes(currentDefaultLanguage)) {
    nextDefaultLanguage = currentDefaultLanguage;
  }

  const nextDefaultInput = getDefaultLanguageInput(nextDefaultLanguage);
  if (nextDefaultInput) {
    nextDefaultInput.checked = true;
  }

  updateEventLanguageCardsState();
}

export function initEventLanguageSettingsForm() {
  renderEventLanguageSettings();

  $(document).on('change', `${EVENT_LANGUAGE_SETTINGS_SELECTOR} .event-language-enabled`, () => {
    syncEventLanguageDefaultSelection();
  });

  $(document).on('change', `${EVENT_LANGUAGE_SETTINGS_SELECTOR} .event-language-default`, (event) => {
    const input = event.currentTarget;
    if (!input.disabled) {
      syncEventLanguageDefaultSelection(input.dataset.languageCode);
    }
  });
}

export function applyEventLanguageSettingsToForm(rawLanguageSettings = getDefaultLanguageSettings()) {
  renderEventLanguageSettings();
  const { availableLanguages, defaultLanguage } = normalizeLanguageSettings(rawLanguageSettings);

  getSystemLanguageOptions().forEach(({ code }) => {
    const enabledInput = getEnabledLanguageInput(code);
    const defaultInput = getDefaultLanguageInput(code);

    if (!enabledInput || !defaultInput) {
      return;
    }

    enabledInput.checked = availableLanguages.includes(code);
    defaultInput.checked = defaultLanguage === code;
  });

  syncEventLanguageDefaultSelection(defaultLanguage);
}

export function collectEventLanguageSettingsFromForm() {
  const availableLanguages = getEnabledLanguageCodesFromForm();

  if (availableLanguages.length === 0) {
    $(EVENT_LANGUAGE_TAB_SELECTOR).tab('show');
    alertMessagePopup(EVENT_LANGUAGE_VALIDATION_MESSAGE);
    return;
  }

  const defaultLanguage = getCheckedDefaultLanguageFromForm({ includeDisabled: false }) || availableLanguages[0];
  return normalizeLanguageSettings({ availableLanguages, defaultLanguage });
}
